import { useMemo, useState } from 'react';
import { ArrowDown2, ArrowUp2, InfoCircle, Refresh } from 'iconsax-reactjs';
import { GAP_SCAN_BARS, TIMEFRAME_SECONDS, type GapMode, type Timeframe, timeframeShort } from '@pipyar/core';
import { useTheme } from '@/lib/useTheme';
import { useSymbol } from '@/lib/useSymbol';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { CandleChart } from '@/components/chart/CandleChart';
import { ChartFrame } from '@/components/chart/ChartFrame';
import { useCandles, useGaps, useGapSummary } from './api';
import { useI18n } from '@/lib/i18n';
import { GapIcon } from '@/components/GapIcon';

/**
 * Minimum gap sizes offered as filters, in pips.
 *
 * Deliberately fine-grained at the low end: measured against real gold data,
 * every true M1 gap sits between 0.1 and 1.1 pips, so presets starting at 5
 * filtered out every one of them and made the filter look broken. The larger
 * values are for the session gaps, which run to tens of pips.
 */
const MIN_PIPS_PRESETS = [0, 1, 5, 25];

/**
 * Gaps are read on M1 and nowhere else.
 *
 * A gap is a range no trade passed through — a property of the feed, not of a
 * timeframe. M1 is the finest series stored, so it is the only one that sees
 * them as they happened; a coarser series shows the same gaps with their edges
 * rounded to its own bar grid, which is a blurrier copy rather than a second
 * opinion.
 */
const GAP_TF: Timeframe = 'M1';

/**
 * The chart loads exactly the window gaps are detected over.
 *
 * It used to load 24 hours while detection scanned a week, so most of the
 * listed gaps had no originating candle on screen and were drawn from the left
 * edge. Loading the same span means every gap can start at the bar that made
 * it, which is what lets the age colours be read against the price action.
 *
 * Counted in bars back from the newest one rather than from the clock, so over
 * a weekend the chart ends on the last trading day instead of an empty window.
 */
const CHART_BARS = GAP_SCAN_BARS[GAP_TF];

/**
 * How much of that window is in view on open.
 *
 * Fitting a month of M1 bars would draw each a fraction of a pixel wide, so the
 * opening view is the last day and the rest is there to scroll back through.
 */
const VISIBLE_BARS = Math.ceil((24 * 3600) / TIMEFRAME_SECONDS[GAP_TF]);

export function GapsPage() {
  const { theme } = useTheme();
  const timeframe = GAP_TF;
  const [minPips, setMinPips] = useState(0);
  // True unfilled gaps are genuinely rare on gold, so being able to see the
  // filled ones is what makes the list worth opening at all.
  const [showFilled, setShowFilled] = useState(false);
  const [mode, setMode] = useState<GapMode>('untraded');

  // The pair comes from the top-bar picker, shared across every module.
  const { symbol, isLoading: symbolLoading } = useSymbol();

  const candles = useCandles(symbol, timeframe, CHART_BARS);
  const gaps = useGaps(symbol, timeframe, { minPips, includeFilled: showFilled, mode });
  const summary = useGapSummary(symbol, minPips, mode);

  const digits = candles.data?.digits ?? 2;

  // The forming bar is appended so the newest candle is not a minute stale.
  // It is deliberately NOT fed to gap detection: a gap against a bar that is
  // still moving would appear and vanish as price ticks.
  const chartCandles = useMemo(() => {
    const base = candles.data?.candles ?? [];
    const live = candles.data?.live;
    return live ? [...base, live] : base;
  }, [candles.data]);

  const allGaps = gaps.data?.gaps ?? [];
  const openGaps = allGaps.filter((g) => !g.filled);
  const below = openGaps.filter((g) => g.direction === 'up');
  const above = openGaps.filter((g) => g.direction === 'down');

  const isBusy = candles.isFetching || gaps.isFetching;
  const error = candles.error ?? gaps.error;

  if (!symbolLoading && !symbol) {
    return <EmptyState />;
  }

  const { t } = useI18n();

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('gaps.title')}</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {symbol ?? '—'} · gap levels extend right from the bar that made them:{' '}
            <span className="font-medium text-red-400">today</span> in red,{' '}
            <span className="font-medium text-blue-400">earlier</span> in blue — filled ones dashed
          </p>
        </div>

        <div className="ms-auto flex items-center gap-2">
          <div className="hidden items-center gap-1 sm:flex">
            <span className="me-1 text-xs text-muted-foreground">{t('gaps.minSize')}</span>
            {MIN_PIPS_PRESETS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setMinPips(p)}
                className={cn(
                  'rounded-md px-2 py-1 text-xs font-medium transition-colors',
                  minPips === p
                    ? 'bg-primary/15 text-primary'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                )}
              >
                {p === 0 ? 'All' : `${p}p`}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-0.5 rounded-md bg-elevated p-0.5">
            {(['untraded', 'range'] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                title={
                  m === 'untraded'
                    ? "Close to the next bar's low (up) or high (down) - the band that never traded"
                    : "Strict: the two bars' ranges do not overlap at all"
                }
                className={cn(
                  'rounded px-2 py-0.5 text-xs font-medium transition-colors',
                  mode === m
                    ? 'bg-primary/15 text-primary'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {m === 'untraded' ? 'Untraded' : 'Strict'}
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={() => setShowFilled((v) => !v)}
            aria-pressed={showFilled}
            className={cn(
              'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
              showFilled
                ? 'bg-primary/15 text-primary'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            Filled
          </button>

          <Button
            variant="ghost"
            size="icon"
            aria-label={t('common.refresh')}
            onClick={() => {
              void candles.refetch();
              void gaps.refetch();
              void summary.refetch();
            }}
          >
            <Refresh size={18} className={isBusy ? 'animate-spin' : undefined} />
          </Button>
        </div>
      </header>

      {error && (
        <div
          role="alert"
          className="flex gap-2.5 rounded-lg border border-danger/30 bg-danger/8 p-3 text-sm text-danger"
        >
          <InfoCircle size={18} variant="Bold" className="mt-px shrink-0" />
          <span>{error instanceof Error ? error.message : 'Could not load gap data.'}</span>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[1fr_18rem]">
        {/* ---------- chart ---------- */}
        {/* min-w-0 is load-bearing: a 1fr grid column defaults to
            min-width:auto, so it cannot shrink below its content. Once the
            chart canvas has been sized to the viewport by full screen, the
            column would stay that wide on exit and the page would scroll
            sideways forever. */}
        <section className="order-2 min-w-0 space-y-3 lg:order-1">
          <div className="flex flex-wrap items-center gap-1">
            <div className="ms-auto flex items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <span className="h-0.5 w-4 rounded bg-[#fde047]" />
                below price
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-0.5 w-4 rounded bg-[#fbbf24]" />
                above price
              </span>
            </div>
          </div>

          <ChartFrame
            label={t('gaps.chart')}
            className="group/frame h-[32rem] overflow-hidden glass rounded-2xl"
          >
            {candles.isPending ? (
              <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                Loading candles…
              </div>
            ) : chartCandles.length === 0 ? (
              <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                No candles for {timeframeShort(timeframe)} yet.
              </div>
            ) : (
              <CandleChart
                className="h-full w-full"
                candles={chartCandles}
                gaps={allGaps}
                storedGmtOffsetSec={candles.data?.storedGmtOffsetSec ?? 0}
                visibleBars={VISIBLE_BARS}
                digits={digits}
                theme={theme}
                resetKey={`${symbol}-${timeframe}`}
                symbol={symbol}
                timeframe={timeframe}
              />
            )}
          </ChartFrame>

          <p className="text-xs text-muted-foreground">
            Scanning the last {GAP_SCAN_BARS[timeframe].toLocaleString()}{' '}
            {timeframeShort(timeframe)} bars ·{' '}
            {candles.data?.count.toLocaleString() ?? 0} loaded ·{' '}
            {gaps.data?.totalDetected ?? 0} gaps found, {openGaps.length} still open · times shown
            on the broker clock (GMT
            {formatOffset(candles.data?.storedGmtOffsetSec ?? 0)})
          </p>
        </section>

        {/* ---------- sidebar ---------- */}
        <aside className="order-1 space-y-4 lg:order-2">
          <div className="glass rounded-2xl p-3">
            <h2 className="mb-2.5 text-sm font-medium">{t('gaps.levels', { timeframe: timeframeShort(timeframe) })}</h2>

            <GapList
              title={t('gaps.belowPrice')}
              icon={<ArrowDown2 size={13} variant="Bold" className="text-bear" />}
              gaps={below}
              digits={digits}
            />
            <div className="my-2.5 h-px bg-border" />
            <GapList
              title={t('gaps.abovePrice')}
              icon={<ArrowUp2 size={13} variant="Bold" className="text-bull" />}
              gaps={above}
              digits={digits}
            />
          </div>
        </aside>
      </div>
    </div>
  );
}

function GapList({
  title,
  icon,
  gaps,
  digits,
}: {
  title: string;
  icon: React.ReactNode;
  gaps: { id: string; level: number; sizePips: number; time: number }[];
  digits: number;
}) {
  // Nearest to current price first — that is the one price reaches next.
  const ordered = [...gaps].reverse().slice(0, 8);

  return (
    <div>
      <div className="mb-1.5 flex items-center gap-1.5 text-xs text-muted-foreground">
        {icon}
        {title}
        <span className="ms-auto tabular">{gaps.length}</span>
      </div>
      {ordered.length === 0 ? (
        <p className="text-xs text-muted-foreground/70">None</p>
      ) : (
        <ul className="space-y-1">
          {ordered.map((g) => (
            <li key={g.id} className="flex items-baseline justify-between text-xs">
              <span className="tabular font-medium">{g.level.toFixed(digits)}</span>
              <span className="tabular text-muted-foreground">{g.sizePips}p</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function EmptyState() {
  const { t } = useI18n();

  return (
    <div className="flex min-h-[24rem] flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-surface/40 p-10 text-center">
      <div className="flex size-12 items-center justify-center rounded-xl bg-muted text-muted-foreground">
        <GapIcon size={24} />
      </div>
      <h2 className="mt-4 text-base font-medium">{t('gaps.noInstruments')}</h2>
      <p className="mt-1 max-w-sm text-sm text-muted-foreground">
        Nothing has been ingested. Check that MT5 is connected and the exporter service is
        running.
      </p>
    </div>
  );
}

function formatOffset(seconds: number): string {
  const sign = seconds < 0 ? '-' : '+';
  const hours = Math.abs(seconds) / 3600;
  return `${sign}${Number.isInteger(hours) ? hours : hours.toFixed(1)}`;
}
